import { bijectionCompose } from "@redotech/util/bijection";
import { StringFormat } from "@redotech/util/string-format";
import { Big } from "big.js";
import { JsonFormat } from "./format";
import { Json, jsonStringFormat } from "./json";

export type Serializable =
  | null
  | undefined
  | string
  | number
  | boolean
  | bigint
  | Big
  | Date
  | Temporal.Instant
  | Temporal.PlainDate
  | Serializable[]
  | readonly Serializable[]
  | Map<Serializable, Serializable>
  | { [key: string]: Serializable }
  | {};

export interface ObjectSerializationModule<
  T extends string = string,
  V extends object = object,
> {
  prefix: T;
  constructor: new (...args: any[]) => V;
  hydrate: (value: { [K in T]: any }, globalHydrate: (value: any) => any) => V;
  dehydrate: (
    value: V,
    globalDehydrate: (value: V) => Json,
  ) => { [K in T]: any };
}

export function createSerializationModule<
  T extends string,
  V extends object = object,
>(
  prefix: T,
  constructor: new (...args: any[]) => V,
  hydrate: (value: { [K in T]: any }, globalHydrate: (value: any) => any) => V,
  dehydrate: (
    value: V,
    globalDehydrate: (value: V) => Json,
  ) => { [K in T]: any },
): ObjectSerializationModule<T, V> {
  return { prefix, constructor, hydrate, dehydrate };
}

const bigModule = createSerializationModule(
  "$$big",
  Big,
  (value) => {
    const bigSource = value.$$big;
    if (typeof bigSource !== "string" && typeof bigSource !== "number") {
      throw new TypeError(
        `Cannot hydrate value of type { $$big: ${typeof bigSource} }`,
      );
    }
    return new Big(bigSource);
  },
  (value) => ({ $$big: value.toString() }),
);

const dateModule = createSerializationModule(
  "$$date",
  Date,
  (value) => {
    const dateSource = value.$$date;
    if (typeof dateSource !== "string" && typeof dateSource !== "number") {
      throw new TypeError(
        `Cannot hydrate value of type { $$date: ${typeof dateSource} }`,
      );
    }
    return new Date(dateSource);
  },
  (value) => ({ $$date: value.toISOString() }),
);

const temporalPlainDateModule = createSerializationModule(
  "$$plaindate",
  Temporal.PlainDate,
  (value) => {
    const plainDate = value.$$plaindate;
    if (typeof plainDate !== "string") {
      throw new TypeError(
        `Cannot hydrate value of type { $$plaindate: ${typeof plainDate} }`,
      );
    }
    return Temporal.PlainDate.from(plainDate);
  },
  (value) => ({ $$plaindate: value.toString() }),
);

const temporalInstantModule = createSerializationModule(
  "$$instant",
  Temporal.Instant,
  (value) => {
    const instantSource = value.$$instant;
    if (typeof instantSource !== "number") {
      throw new TypeError(
        `Cannot hydrate value of type { $$instant: ${typeof instantSource} }`,
      );
    }
    return Temporal.Instant.fromEpochMilliseconds(instantSource);
  },
  (value) => ({ $$instant: value.epochMilliseconds }),
);

const mapModule = createSerializationModule(
  "$$map",
  Map,
  (value, globalHydrate) => {
    const mapSource = value.$$map;
    if (!Array.isArray(mapSource)) {
      throw new TypeError(
        `Cannot hydrate value of type { $$map: ${typeof mapSource} }`,
      );
    }
    if (
      mapSource.some(
        (element) => !Array.isArray(element) || element.length !== 2,
      )
    ) {
      throw new TypeError(
        `Cannot hydrate value of type { $$map: ${mapSource} }`,
      );
    }

    return new Map(
      (mapSource as [Json, Json][]).map(([key, val]) => [
        globalHydrate(key),
        globalHydrate(val),
      ]),
    );
  },
  (value, globalDehydrate) => {
    return {
      $$map: Array.from(value.entries()).map(([key, val]) => [
        globalDehydrate(key),
        globalDehydrate(val),
      ]),
    };
  },
);

export function generateJsonFormat<
  Modules extends ObjectSerializationModule<any, any>[],
>(...modules: Modules): JsonFormat<Serializable> {
  const allModules = [
    bigModule,
    dateModule,
    temporalInstantModule,
    temporalPlainDateModule,
    mapModule,
    ...modules,
  ];

  const prefixToModule = new Map<string, ObjectSerializationModule<any, any>>(
    allModules.map((m) => [m.prefix, m]),
  );

  function dehydrate(value: Serializable | object): Json {
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return value;
    } else if (value === undefined) {
      return { $$undefined: true };
    } else if (typeof value === "bigint") {
      return { $$bigint: value.toString() };
    } else if (Array.isArray(value) || value instanceof Array) {
      // `instanceof Array` is necessary to narrow the readonly[] type - does not affect runtime behavior
      return value.map(dehydrate);
    } else if (typeof value === "object") {
      // Try modules first - check instanceof for each module
      for (const module of allModules) {
        if (value instanceof module.constructor) {
          return module.dehydrate(value as any, dehydrate);
        }
      }
      // Default object serialization
      return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [key, dehydrate(val)]),
      );
    }
    // FIXME Really, we should throw an error here because `value` is not a Serializable type.
    //   But we currently have RPC definitions that include "optional" parameters,
    //   even though we don't consider `undefined` to be serializable.
    //   TypeScript doesn't stop this because the `RPCDefinition` type doesn't require
    //   the input/output zod definitions to be Serializable. We should fix this.
    //throw new TypeError(`Cannot dehydrate value of type ${typeof value}`);
    return value;
  }

  function hydrate(value: Json): Serializable | object {
    switch (typeof value) {
      case "undefined":
      case "function":
      case "symbol":
        throw new TypeError(`Cannot hydrate value of type ${typeof value}`);
      case "string":
      case "number":
      case "boolean":
      case "bigint":
        return value;
      case "object": {
        if (value === null) {
          return value;
        }
        if (Array.isArray(value)) {
          return value.map(hydrate);
        }
        // Try modules first for single-key objects
        if (Object.keys(value).length === 1) {
          const [key, val] = Object.entries(value)[0];
          if (key === "$$undefined") {
            return undefined;
          }
          if (key === "$$bigint" && typeof val === "string") {
            return BigInt(val);
          }
          const module = prefixToModule.get(key);
          if (module) {
            return module.hydrate(value, hydrate);
          }
        }
        // Default object hydration
        return Object.fromEntries(
          Object.entries(value).map(([key, val]) => [key, hydrate(val)]),
        );
      }
      default:
        return value;
    }
  }

  return { read: hydrate, write: dehydrate };
}

// Default format with built-in modules
export const serializeJsonFormat: JsonFormat<Serializable> =
  generateJsonFormat();

export const serializeFormat: StringFormat<Serializable> = bijectionCompose(
  jsonStringFormat,
  serializeJsonFormat,
);

export function stableStringifyJson(serializable: Serializable): string {
  if (
    typeof serializable === "object" &&
    serializable !== null &&
    !Array.isArray(serializable)
  ) {
    // sort keys to ensure stable serialization
    const keys = Object.keys(serializable).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringifyJson(serializable[key as keyof typeof serializable])}`).join(",")}}`;
  } else {
    return JSON.stringify(serializeJsonFormat.write(serializable));
  }
}
